import moment from 'moment';

import { CURRENT_SUNDAY_TERMS } from './sundayTerms';

// Billing B1.5. Yedding's team does not work on Sundays, so every Sunday is
// blocked on every venue unless:
//   1. the host opened Sundays for their account and agreed to the current
//      Sunday terms (users/{id}.sundays, copied to each venue as venue.sundays), or
//   2. the host made no choice and an admin turned all Sundays on
//      (settings/sundays.allOpen, see src/firebase/settings.js).
// A host's own choice always wins over the admin switch.
// Data shape: docs/SUNDAYS.md.

// B1.5b: the host Sunday switch is hidden until early 2027 (shelved, Braeden
// 2026-10-24). While false, only the admin switch counts: a host's saved
// choice is ignored, here and on the server (functions/helper/sundays.js has
// the same flag; a test checks they match). To bring it back: true in both.
export const HOST_SUNDAY_SWITCH = false;

// Every host must agree again when this changes (new file in ./sundayTerms).
export const SUNDAY_TERMS_VERSION = CURRENT_SUNDAY_TERMS.version;

// `date` is a 'YYYY-MM-DD' string or a Date for a calendar day. The day is
// read in the browser's own time zone, the same way the calendar draws it.
export const isSunday = (date) => {
  const day = typeof date === 'string' ? moment(date, 'YYYY-MM-DD') : moment(date);
  return day.isValid() && day.day() === 0;
};

export const rangeHasSunday = (startDate, endDate) => {
  const start = moment(startDate).startOf('day');
  const end = moment(endDate || startDate).startOf('day');
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return false;
  }
  // a range of 7 days or more always has a Sunday
  if (end.diff(start, 'days') >= 6) {
    return true;
  }
  for (const day = moment(start); !day.isAfter(end); day.add(1, 'day')) {
    if (day.day() === 0) {
      return true;
    }
  }
  return false;
};

// true or false when the host made a choice; null when not, or when the host
// switch is off. Works on a venue (venue.sundays) or a host's user record
// (user.sundays): same fields.
export const hostSundayChoice = (record, hostSwitchOn = HOST_SUNDAY_SWITCH) => {
  if (!hostSwitchOn) {
    return null;
  }
  const sundays = record?.sundays;
  if (!sundays || typeof sundays.open !== 'boolean') {
    return null;
  }
  if (sundays.open && sundays.termsVersion !== SUNDAY_TERMS_VERSION) {
    // agreed to old terms: closed until the host agrees to the new ones,
    // even when the admin switch is on (Sunday Terms, section 9)
    return false;
  }
  return sundays.open;
};

export const venueOpensSundays = (venue, allSundaysOpen = false, hostSwitchOn = HOST_SUNDAY_SWITCH) => {
  const choice = hostSundayChoice(venue, hostSwitchOn);
  return choice === null ? allSundaysOpen === true : choice;
};

// What each venue of the host stores: only what search and the calendar need.
// The agreement and the on-site contact stay in the host's private user record.
export const venueSundaysFlag = (open) => ({ open: open === true, termsVersion: SUNDAY_TERMS_VERSION });

// US phone: 10 digits, with or without +1, spaces, dashes, dots or brackets.
// Returns '+1XXXXXXXXXX', or null if it is not a US phone number.
export const normalizePhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  const national = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (national.length !== 10 || /^[01]/.test(national)) {
    return null;
  }
  return `+1${national}`;
};

export const formatPhone = (phone) => {
  const normalized = normalizePhone(phone);
  if (!normalized) {
    return phone || '';
  }
  const n = normalized.slice(2);
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
};

export const cleanContact = ({ name, phone } = {}) => {
  const cleanName = String(name || '')
    .replace(/\s+/g, ' ')
    .trim();
  const cleanPhone = normalizePhone(phone);
  if (cleanName.length < 2 || !cleanPhone) {
    return null;
  }
  return { name: cleanName, phone: cleanPhone };
};

// The fields set on users/{hostId}.sundays, and the history line to add.
// `at` is the browser's time; the database adds its own (updatedAt).
export const sundaysChange = ({ action, current, contact, byUserId, now = new Date() }) => {
  const at = now.toISOString();
  const by = byUserId || null;
  const base = { ...(current || {}) };
  delete base.history;
  delete base.updatedAt;
  let fields;
  if (action === 'open') {
    fields = { ...base, open: true, termsVersion: SUNDAY_TERMS_VERSION, acceptedAt: at, acceptedBy: by, contact };
  } else if (action === 'close') {
    fields = { ...base, open: false, closedAt: at, closedBy: by };
  } else {
    fields = { ...base, contact };
  }
  const entry = { action, at, by, termsVersion: fields.termsVersion || null };
  if (action !== 'close') {
    entry.contact = contact;
  }
  return { fields, entry };
};
